import "@app/App.css";
import { ValueEditor } from "@app/fields/ValueEditor";
import { Button } from "@comps/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { Label } from "@comps/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { ScrollArea } from "@comps/ui/scroll";
import { Separator } from "@comps/ui/separator";
import { Textarea } from "@comps/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import Field, { AggregationType, FIELD_TYPES_WITH_FORMULA, FieldType, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_OBJECT_KEY, TYPE_ITEMS_RELATION_CARDINALITY, TYPE_ITEMS_RELATION_FILTER, TYPE_ITEMS_RELATION_KEY, TYPE_ITEMS_RELATION_NAME, fieldTypeToIcon, hasDefaultValueAfterTypeItems, hasDefaultValueBeforeTypeItems, hasFormatting, validAggregationByType } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { ObjectRelation, getDestCard, getSrcCard } from "@dto/relation";
import { zodResolver } from "@hookform/resolvers/zod";
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { checkFormula } from "@utils/model";
import { TW_AddFieldButton, TW_AddFieldButtonIcon, TW_AddFieldButtonText, TW_AddFieldDefaultValueLabel, TW_AddFieldDefaultValueTitle, TW_AddFieldDescriptionLabel, TW_AddFieldDescriptionTitle, TW_AddFieldForm, TW_AddFieldNameInput, TW_AddFieldPopoverContent, TW_AddFieldPopoverContentWide, TW_AddFieldTypeArrow, TW_AddFieldTypeDiv, TW_AddFieldTypeIcon, TW_AddFieldTypeItem, TW_AddFieldTypeItemSelected, TW_AddFieldTypeScrollArea, TW_AddFieldTypeSeparator, TW_AddFieldTypeText, TW_AppDialogButton, TW_ButtonList, TW_CheckboxCell_not_selected, TW_CheckboxCell_selected, TW_CommonCommandInput, TW_ErrorMessage, TW_FieldDescription_Input, TW_FieldSection, TW_FieldSection_Label, getWsButtonStyle, useHover } from "@utils/twClasses";
import { post } from "@utils/utils";
import { ChevronDown, Minus, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";
import { z } from "zod";
import { FIELD_TYPES_WITH_AGGREGATION, FieldAggregationForm } from "./FieldAggregationForm";
import { FieldFormatSection } from "./FieldFormatSection";
import { FieldFormulaSection } from "./FieldFormulaSection";
import { keepsFormula } from "./fieldTypeChange";
import { FIELD_TYPES_WITH_TYPE_ITEMS, FieldTypeItemsForm } from "./FieldTypeItemsForm";
import { newRelationKey } from "./LinkTypeItemsForm";


export interface FieldFormSchemaType {
  fieldName: string;
  fieldDescription: string | undefined;
  fieldType: FieldType | undefined;
  fieldFormat: Map<string, any>;
  fieldTypeItems: Map<string, any>;
  fieldAggregation: AggregationType | undefined;
  fieldFormula: string | undefined;
  fieldReadOnly: boolean;
}

function AddFieldButton({ className, style }: { className?: string, style?: React.CSSProperties }) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasRelationFilter, setHasRelationFilter] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const hover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));
  const objectInfos = useModelStore(state => state.selectedObjectInfos);
  const rowActionsEnabled = useDataStore(state => state.rowActionsEnabled);
  const hasFields = objectInfos?.fields && objectInfos?.fields.length > 0;

  const fieldAdded = (field: Field) => {
    if (!rowActionsEnabled) {
      return;
    }
    setIsDialogOpen(false);
  }

  function cancel() {
    setIsDialogOpen(false);
  }

  const isDisabled = !rowActionsEnabled
  return (
    <TooltipProvider>
      <Popover open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <PopoverTrigger className={TW_AddFieldButton + (className ? " " + className : "")} {...hover} style={style} disabled={isDisabled}>
          <Tooltip>
            <TooltipTrigger className={"border-none outline-none focus:outline-none hover:outline-none w-full"}>
              {hasFields && <Plus className={TW_AddFieldButtonIcon + (rowActionsEnabled ? " opacity-100 cursor-pointer" : " opacity-50 cursor-not-allowed")} />}
              {!hasFields &&
                <div className={`flex flex-row items-center justify-center gap-2 px-2 py-1 ${rowActionsEnabled ? "cursor-pointer opacity-100" : "cursor-not-allowed opacity-50"}`}>
                  <Plus className={TW_AddFieldButtonIcon} />
                  <span className={TW_AddFieldButtonText}>{t("field.button.add.label")}</span>
                </div>
              }
            </TooltipTrigger>
            <TooltipContent>
              {t("field.button.add.tooltip")}
            </TooltipContent>
          </Tooltip>
        </PopoverTrigger>
        <PopoverContent className={hasRelationFilter ? TW_AddFieldPopoverContentWide : TW_AddFieldPopoverContent}>
          <NewFieldForm onNewField={fieldAdded} onCancel={cancel} onHasRelationFilterChange={setHasRelationFilter} />
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  )
}

function NewFieldForm({ onNewField, onCancel, onHasRelationFilterChange }: { onNewField: (field: Field) => void, onCancel: () => void, onHasRelationFilterChange: (hasFilter: boolean) => void }) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<FieldType | undefined>(undefined);
  const [validAggregationTypes, setValidAggregationTypes] = useState<AggregationType[]>([]);
  const updateSelectedObjectInfos = useModelStore(state => state.updateSelectedObjectInfos);
  const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
  const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
  const fetchData = useDataStore(state => state.fetchData);
  const wsColor = useModelStore(state => state.wsColor);
  const wsLightColor = useModelStore(state => state.wsLightColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const confirmHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const [hasDescription, setHasDescription] = useState(false);
  const [hasDefaultValue, setHasDefaultValue] = useState(false);

  useEffect(() => {
    setValidAggregationTypes(validAggregationByType(selectedType, form.getValues("fieldTypeItems")));
  }, [selectedType]);

  const formSchema = z.object({
    fieldName: z.string().min(1, {
      message: t("field.dialog.error.name.missing"),
    }),
    fieldDescription: z.string().optional(),
    fieldType: z.nativeEnum(FieldType).optional(),
    fieldFormat: z.map(z.string(), z.any()).optional(),
    fieldTypeItems: z.map(z.string(), z.any()).optional(),
    fieldAggregation: z.nativeEnum(AggregationType).optional(),
    fieldFormula: z.string().optional(),
    fieldReadOnly: z.boolean().optional(),
    fieldDefaultValue: z.any().optional(),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      fieldName: "",
      fieldDescription: "",
      fieldType: undefined,
      fieldFormat: new Map<string, any>(),
      fieldTypeItems: new Map<string, any>(),
      fieldAggregation: AggregationType.NO_AGGR,
      fieldFormula: "",
      fieldReadOnly: false,
      fieldDefaultValue: undefined,
    },
    resolver: zodResolver(formSchema),
  })

  function submitRelationField(values: z.infer<typeof formSchema>, order: number) {

    const tItems = values.fieldTypeItems;

    if (!tItems) {
      setError(t("field.dialog.error.link.destination.missing"));
      return;
    }

    const objectKey = tItems.get(TYPE_ITEMS_OBJECT_KEY);
    const fieldKey = tItems.get(TYPE_ITEMS_FIELD_KEY);
    const relationName = tItems.get(TYPE_ITEMS_RELATION_NAME);
    const relationCardinality = tItems.get(TYPE_ITEMS_RELATION_CARDINALITY);
    let relationFilter = tItems.get(TYPE_ITEMS_RELATION_FILTER);
    let relationKey = tItems.get(TYPE_ITEMS_RELATION_KEY);
    if (!objectKey || !fieldKey) {
      setError(t("field.dialog.error.link.destination.missing"));
      return;
    }
    if (!relationKey) {
      setError(t("field.dialog.error.link.relation.key.missing"));
      return;
    }

    if (relationFilter && relationFilter.groupFilter && relationFilter.groupFilter.filters.length === 0) {
      relationFilter = null;
    }

    if (relationKey === newRelationKey) {
      if (!relationName) {
        setError(t("field.dialog.error.link.relation.name.missing"));
        return;
      }

      const relationBody = {
        name: relationName,
        _from: selectedObjectInfos!.object._key,
        _to: objectKey,
        srcCard: getSrcCard(relationCardinality),
        destCard: getDestCard(relationCardinality),
        srcFilter: relationFilter ?? null,
        destFilter: relationFilter ?? null,
      }
      post<ObjectRelation>(`${BASE_URL}/model/workspaces/${workspace!._key}/relations`, relationBody).then(newRelation => {
        if (isCompozzError(newRelation)) {
          const cError = newRelation as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {
          setError(null);
          relationKey = (newRelation as ObjectRelation)?._key;

          if (!relationKey) {
            setError(t("field.dialog.error.link.relation.key.missing"));
            return;
          }

          const newFieldTypeItems = new Map<string, any>(values.fieldTypeItems)
          newFieldTypeItems.set(TYPE_ITEMS_RELATION_KEY, relationKey!)
          newFieldTypeItems.delete(TYPE_ITEMS_RELATION_NAME)
          newFieldTypeItems.delete(TYPE_ITEMS_RELATION_CARDINALITY)
          newFieldTypeItems.delete(TYPE_ITEMS_RELATION_FILTER)
          const newFieldValues: z.infer<typeof formSchema> = { ...values, fieldTypeItems: newFieldTypeItems };
          const newRelations = [...(selectedObjectInfos!.relations || []), newRelation!];
          const newObjectInfos = { ...selectedObjectInfos!, relations: newRelations };
          submitField(newObjectInfos, newFieldValues, order);
        }
      })
    } else {
      submitField(selectedObjectInfos!, values, order);
    }
  }


  function submitField(objectInfos: ObjectInfos, values: z.infer<typeof formSchema>, order: number) {

    const body: Field = {
      _key: "",
      name: values.fieldName,
      type: values.fieldType!,
      readOnly: values.fieldReadOnly || false,
      formula: values.fieldFormula || "",
      order: order,
      format: values.fieldFormat,
      typeItems: values.fieldTypeItems,
      aggregation: values.fieldAggregation,
      description: values.fieldDescription,
      defaultValue: values.fieldDefaultValue ?? undefined,
    }

    post<Field>(`${BASE_URL}/model/workspaces/${workspace!._key}/objects/${objectInfos!.object._key}/fields`, body).then(newField => {
      if (isCompozzError(newField)) {
        const cError = newField as CompozzError;
        if (cError.i18n) {
          setError(translate(t, cError.i18n, cError.params));
        } else {
          setError(cError.message);
        }
      } else {
        setError(null);

        if (objectInfos) {
          const newFields = (objectInfos.fields) ? [...objectInfos.fields, newField] : [newField];
          const newSelectedObjectInfos = { ...objectInfos, fields: newFields };
          updateSelectedObjectInfos(newSelectedObjectInfos as ObjectInfos, null, null, sharedObjectInfos);
        }
        fetchData();

        onNewField(newField as Field);
      }
    })

  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    setError(null);
    if (!workspace || !selectedObjectInfos) {
      console.error("No workspace or object selected");
      return;
    }

    let order = 0;
    if (selectedObjectInfos.fields && selectedObjectInfos.fields.length > 0) {
      const lastFields = selectedObjectInfos.fields[selectedObjectInfos.fields.length - 1];
      if (lastFields.order) {
        order = lastFields.order + DEFAULT_ORDER_INCREMENT;
      } else {
        order = DEFAULT_ORDER_INCREMENT;
      }
    }

    if (values.fieldFormula && values.fieldFormula !== "") {
      checkFormula(workspace!._key, selectedObjectInfos!.object._key, undefined, values.fieldFormula, () => {
        if (selectedType === FieldType.Link) {
          submitRelationField(values, order);
        } else {
          submitField(selectedObjectInfos!, values, order);
        }
      }, (errorI18n, _, errorParams) => {
        setError(translate(t, errorI18n, errorParams));
      });
    } else {
      if (selectedType === FieldType.Link) {
        submitRelationField(values, order);
      } else {
        submitField(selectedObjectInfos!, values, order);
      }
    }
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  function onFormatChange(formatKey: string, isCheckbox: boolean) {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      let newValue: any = event.target.value;
      if (isCheckbox && event.target instanceof HTMLInputElement) {
        newValue = event.target.checked;
      }
      const newFormat = new Map(form.getValues("fieldFormat"));
      newFormat.set(formatKey, newValue);
      form.setValue("fieldFormat", newFormat);
    }
  }

  function onFormulaChange(formula: string) {
    form.setValue("fieldFormula", formula);
  }

  function onTypeChange(type: FieldType | undefined) {
    setSelectedType(type);
    form.setValue("fieldType", type);
    form.setValue("fieldTypeItems", new Map<string, any>());
    setValidAggregationTypes(validAggregationByType(type, form.getValues("fieldTypeItems")));
    form.setValue("fieldAggregation", AggregationType.NO_AGGR);
    onHasRelationFilterChange(false);
    form.setValue("fieldDefaultValue", undefined);
    setHasDefaultValue(false);
    if (!keepsFormula(type)) {
      form.setValue("fieldFormula", "");
    }
  }


  function onAggregationChange(aggregationType: AggregationType) {
    form.setValue("fieldAggregation", aggregationType);
  }

  function onTypeItemsChange(typeItems: Map<string, any>) {
    setValidAggregationTypes(validAggregationByType(selectedType, typeItems));
    form.setValue("fieldTypeItems", typeItems);

    // Check if we have a link field with a relation filter
    if (selectedType === FieldType.Link) {
      const relationFilter = typeItems.get(TYPE_ITEMS_RELATION_FILTER);
      onHasRelationFilterChange(relationFilter != null);
    } else {
      onHasRelationFilterChange(false);
    }
  }

  function showAggregation() {
    if (!selectedType) {
      return false;
    }
    if (selectedType !== FieldType.Link) {
      return false;
    }
    return validAggregationTypes.length > 1 && FIELD_TYPES_WITH_AGGREGATION.includes(selectedType);
  }

  const hasAggregation = showAggregation();


  function cancel(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    onCancel();
    onHasRelationFilterChange(false);
  }

  const scrollStyle = {
    scrollbarColor: wsColor + " " + wsLightColor
  }

  const handleAddDescription = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasDescription) {
      form.setValue("fieldDescription", undefined);
    }
    setHasDescription(!hasDescription);
  };

  const handleAddDefaultValue = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasDefaultValue) {
      form.setValue("fieldDefaultValue", undefined);
    }
    setHasDefaultValue(!hasDefaultValue);
  };

  const handleRemoveDefaultValue = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    e.preventDefault();
    form.setValue("fieldDefaultValue", undefined);
    setHasDefaultValue(false);
  };

  function onDefaultValueChange(value: any) {
    const defaultValue = value;
    if (defaultValue === undefined || defaultValue === null || defaultValue === "") {
      form.setValue("fieldDefaultValue", undefined);
    } else {
      form.setValue("fieldDefaultValue", value);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={TW_AddFieldForm}>
        <FormField
          control={form.control}
          name="fieldName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input className={TW_AddFieldNameInput} placeholder={t("field.dialog.name.placeholder")} {...field} onKeyDown={onEnterKeyPress} autoFocus={true} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="fieldType"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <>
                  {field.value && field.value !== undefined && (
                    <Command>
                      <CommandItem
                        className={TW_AddFieldTypeItemSelected}
                        onSelect={() => onTypeChange(undefined)}>
                        {fieldTypeToIcon(field.value, TW_AddFieldTypeIcon)}
                        <span className={TW_AddFieldTypeText}>{t(`field.type.${field.value}`)}</span>
                        <ChevronDown className={TW_AddFieldTypeArrow} />
                      </CommandItem>
                    </Command>
                  )}
                  {field.value === undefined && (
                    <div className={TW_AddFieldTypeDiv}>
                      <Command>
                        <CommandInput placeholder={t("data.type.search.placeholder")} className={TW_CommonCommandInput} />
                        <ScrollArea className={TW_AddFieldTypeScrollArea} style={scrollStyle}>
                          <CommandList className="h-full max-h-full">
                            <CommandEmpty>{t("data.type.search.noResults")}</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                key={FieldType.Link}
                                className={`${selectedType === FieldType.Link ? TW_AddFieldTypeItemSelected : TW_AddFieldTypeItem}`}
                                onSelect={() => onTypeChange(FieldType.Link)}>
                                {fieldTypeToIcon(FieldType.Link, "AddFieldTypeIcon")}
                                <span className="AddFieldTypeText">{t(`field.type.${FieldType.Link}`)}</span>
                              </CommandItem>
                              <Separator className={TW_AddFieldTypeSeparator} />
                              {Object.values(FieldType).filter(type => type !== FieldType.Link).map((type) => (
                                <CommandItem
                                  key={type}
                                  className={`${selectedType === type ? TW_AddFieldTypeItemSelected : TW_AddFieldTypeItem}`}
                                  onSelect={() => onTypeChange(type)}>
                                  {fieldTypeToIcon(type, "AddFieldTypeIcon")}
                                  <span className="AddFieldTypeText">{t(`field.type.${type}`)}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </ScrollArea>
                      </Command>
                    </div>
                  )}
                </>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {selectedType !== undefined && (
          <FormField
            control={form.control}
            name="fieldReadOnly"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className={"flex items-center gap-2 mt-2 w-full" + (selectedType !== undefined && !hasDefaultValueBeforeTypeItems(selectedType) ? " mb-2" : "")}>
                    <Label htmlFor="fieldReadOnly" className="cursor-pointer text-sm grow">{t("field.dialog.readOnly.label")}</Label>
                    <Input className={(field.value ? TW_CheckboxCell_selected : TW_CheckboxCell_not_selected)} type="checkbox" checked={field.value} onChange={field.onChange} />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />)}
        {selectedType !== undefined && hasDefaultValueBeforeTypeItems(selectedType) && !hasDefaultValue && (
          <div className={TW_AddFieldDefaultValueTitle + " mt-0 mb-2 h-9"} onClick={handleAddDefaultValue}>
                <Plus className="text-gray-800 h-4 w-4" />
                <Label className={TW_AddFieldDefaultValueLabel + " cursor-pointer"}>{t("field.dialog.defaultValue.add")}</Label>
          </div>
        )}
        {selectedType !== undefined && hasDefaultValueBeforeTypeItems(selectedType) && hasDefaultValue && (
          <FormField
            control={form.control}
            name="fieldDefaultValue"
            render={({ field }) => (
              <FormItem className="flex justify-start items-center gap-2 mt-0 mb-2 w-full h-9">
                <FormControl >
                  <>
                    <Label htmlFor="fieldDefaultValue" className="text-sm grow ">{t("field.dialog.defaultValue.label")}</Label>
                    <ValueEditor format={form.getValues("fieldFormat") as Map<string, any>} typeItems={form.getValues("fieldTypeItems") as Map<string, any>} type={selectedType} value={field.value} disabled={false} onValueChange={onDefaultValueChange} className="w-44" />
                    <Trash2 className="text-gray-800 h-4 w-4 cursor-pointer" onClick={handleRemoveDefaultValue} />
                  </>
                </FormControl>
              </FormItem>
            )}
          />)}
        {selectedType !== undefined && FIELD_TYPES_WITH_TYPE_ITEMS.includes(selectedType) && (
          <FieldTypeItemsForm field={{ type: selectedType, typeItems: form.getValues("fieldTypeItems") } as Field} form={form as any} onTypeItemsChange={onTypeItemsChange} setError={setError} updateAggregation={onAggregationChange} />
        )}
        {selectedType !== undefined && hasDefaultValueAfterTypeItems(selectedType) && !hasDefaultValue && (
          <div className={TW_AddFieldDefaultValueTitle + " mt-0 mb-2 h-9"} onClick={handleAddDefaultValue}>
                <Plus className="text-gray-800 h-4 w-4" />
                <Label className={TW_AddFieldDefaultValueLabel + " cursor-pointer"}>{t("field.dialog.defaultValue.add")}</Label>
          </div>
        )}
        {selectedType !== undefined && hasDefaultValueAfterTypeItems(selectedType) && hasDefaultValue && (
          <FormField
            control={form.control}
            name="fieldDefaultValue"
            render={({ field }) => (
              <FormItem className="flex justify-start items-center gap-2 mt-0 mb-2 w-full h-9">
                <FormControl>
                  <>
                    <Label htmlFor="fieldDefaultValue" className="text-sm grow w-1/2">{t("field.dialog.defaultValue.label")}</Label>
                    <ValueEditor format={form.getValues("fieldFormat") as Map<string, any>} typeItems={form.getValues("fieldTypeItems") as Map<string, any>} type={selectedType} value={field.value} disabled={false} onValueChange={onDefaultValueChange} />
                    <Trash2 className="text-gray-800 h-4 w-4 cursor-pointer" onClick={handleRemoveDefaultValue} />
                  </>
                </FormControl>
              </FormItem>
            )}
          />)}
        {selectedType !== undefined && hasFormatting({ type: selectedType, typeItems: form.getValues("fieldTypeItems") } as Field) && (
          <FormField
            control={form.control}
            name="fieldFormat"
            render={({ field }) => (
              <div className={TW_FieldSection + " mt-4"}>
                <p className={TW_FieldSection_Label}>{t(`field.format.label`)}</p>
                <FieldFormatSection fieldType={selectedType} fieldTypeItems={form.getValues("fieldTypeItems")} aggregation={form.getValues("fieldAggregation")} format={field.value} onFormatChange={onFormatChange} />
              </div>
            )}
          />
        )}
        {selectedType !== undefined && FIELD_TYPES_WITH_FORMULA.includes(selectedType) && (
          <FormField
            control={form.control}
            name="fieldFormula"
            render={({ field }) => (
              <FieldFormulaSection formula={field.value} onFormulaChange={onFormulaChange} />
            )}
          />
        )}
        {selectedType !== undefined && hasAggregation && (
          <FormField
            control={form.control}
            name="fieldAggregation"
            render={({ field }) => (
              <FieldAggregationForm fieldType={selectedType} fieldTypeItems={form.getValues("fieldTypeItems")} fieldAggregation={field.value} onAggregationChange={onAggregationChange} setError={setError} />
            )}
          />
        )}
        {selectedType !== undefined && hasDescription &&
          <FormField
            control={form.control}
            name="fieldDescription"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className={TW_FieldSection + " mt-2"}>
                    <p className={TW_FieldSection_Label}>{t(`field.dialog.description.label`)}</p>
                    <Textarea className={TW_FieldDescription_Input} style={scrollStyle} placeholder={t("field.dialog.description.placeholder")} {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />}
        {error && (<div className={TW_ErrorMessage}>{error}</div>)}
        {!error && (<div className={TW_ErrorMessage}>&nbsp;</div>)}
        <div className={TW_ButtonList}>
          <div className={TW_AddFieldDescriptionTitle} onClick={handleAddDescription}>
            {hasDescription ? (
              <>
                <Minus className="text-gray-800 h-4 w-4" />
                <Label className={TW_AddFieldDescriptionLabel + " cursor-pointer"}>{t("field.dialog.description.remove")}</Label>
              </>
            ) : (
              <>
                <Plus className="text-gray-800 h-4 w-4" />
                <Label className={TW_AddFieldDescriptionLabel + " cursor-pointer"}>{t("field.dialog.description.add")}</Label>
              </>
            )}
          </div>
          <Button className={TW_AppDialogButton} onClick={cancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...confirmHover}>{t("general.button.ok.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default AddFieldButton;
